// ============================================
// ImageUploadField — ô upload ảnh dùng chung
// ============================================
//
// BE 03/09/2026 bật `image_urls` cho Store / Category / Supplier, cùng hạ tầng
// và cùng tên field với Product. Luồng 2 bước, giống hệt ProductFormModal:
//   1. POST /uploads/images/ với `entity_type` tương ứng → lấy `file_url`
//   2. Form cha PATCH entity kèm `image_urls: [file_url, ...]`
//
// Ràng buộc BE (áp cho cả bốn entity): mảng chuỗi, TỐI ĐA 20 ảnh, chuỗi rỗng
// bị loại bỏ, gửi `null` = xoá hết ảnh. Component này chỉ quản một ảnh (đủ
// cho ba form hiện tại) nhưng nhận/trả MẢNG để khớp BE, khỏi migration lần hai
// khi cần nhiều ảnh.

import { useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import { useUploadImage, type EntityType } from '../../hooks/useUploadImage';

interface ImageUploadFieldProps {
  label?: string;
  /** Giá trị hiện tại từ BE. Mảng rỗng/null = chưa có ảnh (không phải lỗi). */
  value?: string[] | null;
  onChange: (next: string[]) => void;
  entityType: EntityType;
  disabled?: boolean;
  /**
   * `compact` xếp DỌC (nút nằm dưới ô ảnh) và bỏ dòng gợi ý, cho những chỗ
   * ô ảnh phải nằm cạnh các ô nhập khác — như một dòng ở màn tạo hàng loạt.
   * Bản `default` xếp ngang, dùng cho form một entity nơi có cả chiều rộng.
   */
  variant?: 'default' | 'compact';
}

export function ImageUploadField({
  label,
  value,
  onChange,
  entityType,
  disabled = false,
  variant = 'default',
}: ImageUploadFieldProps) {
  const { t } = useI18n();
  const { uploadImage, isUploading, uploadError, setUploadError } = useUploadImage();
  const inputRef = useRef<HTMLInputElement>(null);
  /** Blob cục bộ để xem ngay, không đợi round-trip. */
  const [preview, setPreview] = useState<string | null>(null);

  const currentUrl = preview ?? (value && value.length > 0 ? value[0] : null);
  const compact = variant === 'compact';

  const handleFile = async (file: File) => {
    setUploadError(null);
    const blobUrl = URL.createObjectURL(file);
    setPreview(blobUrl);

    const fileUrl = await uploadImage(file, entityType);
    if (fileUrl) {
      onChange([fileUrl]);
    } else {
      // Upload hỏng: bỏ preview để không hứa một tấm ảnh chưa hề được lưu
      URL.revokeObjectURL(blobUrl);
      setPreview(null);
    }
  };

  const handleRemove = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    // Mảng rỗng — form cha quyết định gửi [] hay null (BE hiểu cả hai)
    onChange([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      {label ? (
        <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      ) : null}

      <div className={compact ? 'flex flex-col items-start gap-1.5' : 'flex items-center gap-3'}>
        <div
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 ${
            compact ? 'h-16 w-16' : 'h-20 w-20'
          }`}
        >
          {isUploading ? (
            <svg className="h-6 w-6 animate-spin text-primary-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : currentUrl ? (
            <img src={currentUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            // Chưa có ảnh là trạng thái HỢP LỆ — placeholder trung tính,
            // không bịa ảnh mẫu.
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          )}
        </div>

        <div className={compact ? 'flex items-center gap-2' : 'flex flex-col gap-1.5'}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || isUploading}
            className={`rounded-lg border border-slate-200 font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 ${
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            }`}
          >
            {currentUrl ? t('imageReplace') : t('imageUpload')}
          </button>
          {currentUrl ? (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || isUploading}
              className={`font-semibold text-red-600 hover:underline disabled:opacity-50 ${
                compact ? 'text-xs' : 'text-left text-xs'
              }`}
            >
              {t('imageRemove')}
            </button>
          ) : null}
        </div>
      </div>

      {uploadError ? <p className="mt-1.5 text-xs text-red-600">{uploadError}</p> : null}
      {/* Gợi ý định dạng/dung lượng chỉ hữu ích ở form một entity. Trong bản
          compact nó bị lặp lại trên từng dòng và đẩy chiều cao lên vô ích. */}
      {!compact && <p className="mt-1.5 text-xs text-slate-400">{t('imageUploadHint')}</p>}
    </div>
  );
}
